import { useCallback, useEffect, useRef, useState } from "react";
import { FileSpreadsheet, FileDown, Loader2, RotateCcw, Upload } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { estApi } from "../lib/api";

const STATUS_STYLE = {
  update: "bg-emerald-50 text-emerald-700 border-emerald-200",
  new: "bg-blue-50 text-blue-700 border-blue-200",
  unchanged: "bg-slate-100 text-slate-500 border-slate-200",
  unmatched: "bg-amber-50 text-amber-700 border-amber-200",
  error: "bg-rose-50 text-rose-700 border-rose-200",
};
const money = (v) => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`);

/** Download the ABS update template, send it back, and only prices move. */
export default function PriceUpdateDialog({ open, onOpenChange, templateParams, onCommitted, onExportCsv }) {
  const [mode, setMode] = useState("prices_only");
  const [createNew, setCreateNew] = useState(false);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [batches, setBatches] = useState([]);
  const fileRef = useRef(null);

  const loadBatches = useCallback(() => {
    estApi.getPriceImportBatches().then((r) => setBatches(r.batches || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!open) return;
    setPreview(null);
    loadBatches();
  }, [open, loadBatches]);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      await estApi.downloadPriceTemplate(templateParams || {}, fmt);
      toast.success(`Template downloaded (${fmt.toUpperCase()})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally {
      setBusy("");
    }
  };

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy("preview");
    try {
      setPreview(await estApi.previewPriceUpdate(file, mode, createNew));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read that file");
    } finally {
      setBusy("");
    }
  };

  const commit = async () => {
    setBusy("commit");
    try {
      const res = await estApi.commitPriceUpdate(preview.staging_id);
      toast.success(`${res.updated} price${res.updated === 1 ? "" : "s"} updated`
        + (res.created ? `, ${res.created} created` : ""));
      setPreview(null);
      loadBatches();
      onCommitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy("");
    }
  };

  const undo = async (batchId) => {
    await estApi.undoPriceImport(batchId).then(
      (r) => toast.success(`Rolled back ${r.reverted} price${r.reverted === 1 ? "" : "s"}`),
      (e) => toast.error(e?.response?.data?.detail || "Undo failed"));
    loadBatches();
    onCommitted?.();
  };

  const c = preview?.counts || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="price-update-dialog">
        <DialogHeader>
          <DialogTitle>Upload pricing</DialogTitle>
          <DialogDescription className="text-xs">
            Download the template (it comes pre-filled with whatever you have filtered), change the
            Unit Cost column, and send it back. Existing parts are matched on ID, then part number +
            manufacturer — nothing else about them moves.
          </DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => download("xlsx")}
                      data-testid="download-template-xlsx-btn">
                {busy === "xlsx" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  : <FileSpreadsheet className="h-4 w-4 mr-1.5" />} Download template (Excel)
              </Button>
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => download("csv")}
                      data-testid="download-template-csv-btn">
                {busy === "csv" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  : <FileSpreadsheet className="h-4 w-4 mr-1.5" />} Download template (CSV)
              </Button>
              {onExportCsv && (
                <Button size="sm" variant="ghost" className="ml-auto" disabled={!!busy} onClick={onExportCsv}
                        title="Export the filtered Cost Library as CSV (full columns, not the pricing template)" data-testid="export-csv-btn">
                  <FileDown className="h-4 w-4 mr-1.5" /> Export CSV
                </Button>)}
            </div>

            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <label className="flex items-start gap-2 text-xs text-slate-700">
                <input type="radio" className="mt-0.5" checked={mode === "prices_only"}
                       onChange={() => { setMode("prices_only"); setCreateNew(false); }}
                       data-testid="mode-prices-only" />
                <span><b>Prices only</b> — applies nothing but Unit Cost. Budget codes, Procore links
                  and cost codes are untouched. Rows that match nothing are reported, never created.</span>
              </label>
              <label className="flex items-start gap-2 text-xs text-slate-700">
                <input type="radio" className="mt-0.5" checked={mode === "full"}
                       onChange={() => setMode("full")} data-testid="mode-full" />
                <span><b>Full update</b> — also applies Name, UOM, Labor Hrs/Unit, Taxable, Budget
                  Code, Cost Code, Vendor and Active. Blank cells are skipped, never blanked out.</span>
              </label>
              {mode === "full" && (
                <label className="flex items-center gap-2 pl-6 text-xs text-slate-600">
                  <input type="checkbox" checked={createNew}
                         onChange={(e) => setCreateNew(e.target.checked)}
                         data-testid="create-new-checkbox" />
                  Create items for rows with a blank ID
                </label>
              )}
            </div>

            <div>
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" className="hidden"
                     onChange={pick} data-testid="price-update-file-input" />
              <Button variant="data" size="sm" disabled={!!busy} onClick={() => fileRef.current?.click()}
                      data-testid="upload-price-update-btn">
                {busy === "preview" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  : <Upload className="h-4 w-4 mr-1.5" />} Upload update
              </Button>
            </div>

            {batches.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-slate-600 mb-1.5">Recent imports</div>
                <div className="rounded-lg border border-slate-200 divide-y divide-slate-100"
                     data-testid="price-import-batches">
                  {batches.map((b) => (
                    <div key={b.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                      <span className="font-medium text-slate-700 truncate flex-1">{b.file_name}</span>
                      <span className="text-slate-500">{b.updated} updated
                        {b.created ? ` · ${b.created} new` : ""}</span>
                      <span className="text-slate-400">{String(b.created_at).slice(0, 10)}</span>
                      {b.undone_at ? (
                        <span className="text-slate-400 italic">undone</span>
                      ) : (
                        <button onClick={() => undo(b.id)}
                                className="inline-flex items-center gap-1 text-[#007BFF] hover:underline"
                                data-testid={`undo-import-${b.id}`}>
                          <RotateCcw className="h-3 w-3" /> Undo
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="price-update-preview">
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              {["update", "new", "unchanged", "unmatched", "error"].map((k) => (
                <span key={k} className={`px-2 py-0.5 rounded-full border ${STATUS_STYLE[k]}`}
                      data-testid={`preview-count-${k}`}>
                  {c[k] || 0} {k}
                </span>
              ))}
              <span className="px-2 py-0.5 rounded-full border border-slate-200 text-slate-500">
                {c.total || 0} rows · {preview.file_name}
              </span>
            </div>
            <div className="max-h-[46vh] overflow-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-slate-500">
                  <tr>
                    {["Row", "Status", "Item #", "Name", "Old", "New", "Note"].map((h) => (
                      <th key={h} className="text-left font-medium px-2 py-1.5">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.rows.map((r) => (
                    <tr key={r.row} data-testid={`preview-row-${r.row}`}>
                      <td className="px-2 py-1.5 text-slate-400">{r.row}</td>
                      <td className="px-2 py-1.5">
                        <span className={`px-1.5 py-0.5 rounded border ${STATUS_STYLE[r.status]}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 font-mono text-slate-600">{r.item_number}</td>
                      <td className="px-2 py-1.5 text-slate-700 max-w-[220px] truncate">{r.name}</td>
                      <td className="px-2 py-1.5 text-slate-500">{money(r.old_cost)}</td>
                      <td className="px-2 py-1.5 font-medium text-slate-900">{money(r.new_cost)}</td>
                      <td className="px-2 py-1.5 text-slate-500">{r.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          {preview && (
            <>
              <Button size="sm" variant="outline" onClick={() => setPreview(null)}
                      data-testid="preview-back-btn">Back</Button>
              <Button size="sm" disabled={busy === "commit" || !((c.update || 0) + (c.new || 0))}
                      onClick={commit} data-testid="commit-price-update-btn">
                {busy === "commit" && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                Apply {(c.update || 0) + (c.new || 0)} change
                {(c.update || 0) + (c.new || 0) === 1 ? "" : "s"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
